import { useEffect, useState, Fragment } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BookOpen, Trash2, Sprout, FlaskConical, Calendar, MapPin,
  ChevronDown, ChevronUp, Printer, Pencil, X, Check,
  ShieldCheck, ShieldAlert, ShieldOff, HelpCircle,
  FileSpreadsheet, FileText,
} from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import AIJournalInput from "../components/AIJournalInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso, locale = "it-IT") =>
  new Date(iso).toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "numeric" });

const toInputDate = (iso) => iso ? iso.slice(0, 10) : "";

const splitProductNames = (name) => {
  if (!name) return [];
  return name.split(/,| e | ed | \+ /i).map((s) => s.trim()).filter(Boolean);
};

// Determina lo stato peggiore di una riga per il semaforo
const STATUS_PRIORITY = { revocato: 3, scaduto: 2, ok: 1, sconosciuto: 0 };
const rowWorstStatus = (productName, statusMap) => {
  const names = splitProductNames(productName);
  let worst = "sconosciuto";
  for (const n of names) {
    const s = (statusMap[n] || {}).status || "sconosciuto";
    if ((STATUS_PRIORITY[s] ?? 0) > (STATUS_PRIORITY[worst] ?? 0)) worst = s;
  }
  return worst;
};

export default function Journal() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "fr" ? "fr-FR" : "it-IT";
  const [treatments, setTreatments] = useState([]);
  const [advice, setAdvice] = useState([]);
  const [fertilizations, setFertilizations] = useState([]);
  const [vineyards, setVineyards] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("register");
  const [expanded, setExpanded] = useState({});
  const [ministryStatus, setMinistryStatus] = useState({});
  const [downloading, setDownloading] = useState(null);
  const [showBackupBanner, setShowBackupBanner] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, fRes, aRes, vRes] = await Promise.all([
        axios.get(`${API}/treatments`, { withCredentials: true }),
        axios.get(`${API}/fertilizations`, { withCredentials: true }),
        axios.get(`${API}/fertilizer/history`, { withCredentials: true }),
        axios.get(`${API}/vineyards`, { withCredentials: true }),
      ]);
      setTreatments(tRes.data);
      setFertilizations(fRes.data);
      setAdvice(aRes.data);
      const map = {};
      vRes.data.forEach((v) => (map[v.id] = v));
      setVineyards(map);

      const productVineyard = new Map(); // nome prodotto -> vineyard_id del trattamento (prima occorrenza)
      tRes.data.forEach((t) => {
        splitProductNames(t.product_name).forEach((n) => {
          if (!productVineyard.has(n)) productVineyard.set(n, t.vineyard_id);
        });
      });
      if (productVineyard.size > 0) {
        try {
          const mRes = await axios.post(
            `${API}/fitosanitari/check`,
            { items: [...productVineyard].map(([name, vineyard_id]) => ({ name, vineyard_id })) },
            { withCredentials: true, timeout: 30000 }
          );
          setMinistryStatus(mRes.data || {});
        } catch (e) { /* silent */ }
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  
// Backup reminder check (every 90 days)
  useEffect(() => {
    if (loading) return;
    if (treatments.length === 0 && fertilizations.length === 0) return;
    const last = localStorage.getItem("backup-reminder-last");
    if (!last) {
      localStorage.setItem("backup-reminder-last", new Date().toISOString());
      return;
    }
    const daysSince = (Date.now() - new Date(last).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSince > 90) setShowBackupBanner(true);
  }, [loading, treatments.length, fertilizations.length]);

  const snoozeBackup = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - (90 - days));
    localStorage.setItem("backup-reminder-last", d.toISOString());
    setShowBackupBanner(false);
    toast.info(days === 1 ? t("journal.snoozeToastTomorrow") : t("journal.snoozeToastDays", { days }));
    };
  const deleteTreatment = async (id) => {
    if (!window.confirm(t("journal.confirmDeleteTreatment"))) return;
    try {
      await axios.delete(`${API}/treatments/${id}`, { withCredentials: true });
      setTreatments((a) => a.filter((x) => x.id !== id));
      toast.success(t("journal.deleteSuccess"));
    } catch { toast.error(t("journal.deleteError")); }
  };

  const deleteFertilization = async (id) => {
    if (!window.confirm(t("journal.confirmDeleteFert"))) return;
    try {
      await axios.delete(`${API}/fertilizations/${id}`, { withCredentials: true });
      setFertilizations((a) => a.filter((x) => x.id !== id));
      toast.success(t("journal.deleteSuccess"));
    } catch { toast.error(t("journal.deleteError")); }
  };

  const deleteAdvice = async (id) => {
    if (!window.confirm(t("journal.confirmDeleteAdvice"))) return;
    try {
      await axios.delete(`${API}/fertilizer/history/${id}`, { withCredentials: true });
      setAdvice((a) => a.filter((x) => x.id !== id));
      toast.success(t("journal.deleteSuccess"));
    } catch { toast.error(t("journal.deleteError")); }
  };

  const openEditTreatment = (t) => {
    setEditTarget("treatment");
    setEditItem(t);
    setEditForm({
      treatment_date: toInputDate(t.treatment_date),
      product_name: t.product_name || "",
      active_ingredient: t.active_ingredient || "",
      quantity_used: t.quantity_used || "",
      surface_treated: t.surface_treated ?? "",
      target_diseases: (t.target_diseases || []).join(", "),
      operator_name: t.operator_name || "",
      notes: t.notes || "",
      siret_exploitant: t.siret_exploitant || "",
      siret_prestataire: t.siret_prestataire || "",
      ilot: t.ilot || "",
      numero_parcella: t.numero_parcella || "",
      codice_insee_comune: t.codice_insee_comune || "",
      bbch_stage: t.bbch_stage || "",
      eppo_target_codes: (t.eppo_target_codes || []).join(", "),
      treatment_start_time: t.treatment_start_time || "",
      treatment_end_time: t.treatment_end_time || "",
    });
  };

  const openEditFertilization = (f) => {
    setEditTarget("fertilization");
    setEditItem(f);
    setEditForm({
      fertilization_date: toInputDate(f.fertilization_date),
      product_name: f.product_name || "",
      product_type: f.product_type || "",
      quantity_used: f.quantity_used || "",
      surface_treated: f.surface_treated ?? "",
      application_method: f.application_method || "",
      operator_name: f.operator_name || "",
      objective: f.objective || "",
      notes: f.notes || "",
    });
  };

  const closeEdit = () => { setEditTarget(null); setEditItem(null); setEditForm({}); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      if (editTarget === "treatment") {
        const payload = {
          ...editForm,
          target_diseases: editForm.target_diseases
            ? editForm.target_diseases.split(",").map((s) => s.trim()).filter(Boolean)
            : [],
          eppo_target_codes: editForm.eppo_target_codes
            ? editForm.eppo_target_codes.split(",").map((s) => s.trim()).filter(Boolean)
            : [],
          surface_treated: editForm.surface_treated !== "" ? Number(editForm.surface_treated) : null,
        };
        const res = await axios.put(`${API}/treatments/${editItem.id}`, payload, { withCredentials: true });
        setTreatments((prev) => prev.map((x) => x.id === editItem.id ? { ...x, ...res.data } : x));
      } else {
        const payload = {
          ...editForm,
          surface_treated: editForm.surface_treated !== "" ? Number(editForm.surface_treated) : null,
        };
        const res = await axios.put(`${API}/fertilizations/${editItem.id}`, payload, { withCredentials: true });
        setFertilizations((prev) => prev.map((x) => x.id === editItem.id ? { ...x, ...res.data } : x));
      }
      toast.success(t("journal.saveSuccess"));
      closeEdit();
    } catch { toast.error(t("journal.saveError")); }
    finally { setSaving(false); }
  };

  const filteredTreat = treatments
    .filter((t) => filter === "all" || t.vineyard_id === filter)
    .sort((a, b) => new Date(b.treatment_date) - new Date(a.treatment_date));

  const filteredAdvice = advice
    .filter((a) => filter === "all" || a.vineyard_id === filter)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const filteredFert = fertilizations
    .filter((f) => filter === "all" || f.vineyard_id === filter)
    .sort((a, b) => new Date(b.fertilization_date) - new Date(a.fertilization_date));

  const fertByYear = {};
  filteredFert.forEach((t) => {
    const y = new Date(t.fertilization_date).getFullYear();
    fertByYear[y] = fertByYear[y] || [];
    fertByYear[y].push(t);
  });

  const byYear = {};
  filteredTreat.forEach((t) => {
    const y = new Date(t.treatment_date).getFullYear();
    byYear[y] = byYear[y] || [];
    byYear[y].push(t);
  });

  const exportPdf = () => { window.print(); };

  const downloadFile = async (format) => {
    if (downloading) return;
    setDownloading(format);
    try {
      const isFrRegistre = format === "registre-fr";
      const endpoint = isFrRegistre ? "/export/registre-fr/excel" : `/export/quaderno/${format}`;
      const ext = isFrRegistre ? "xlsx" : (format === "excel" ? "xlsx" : "pdf");
      const filenamePrefix = isFrRegistre ? "registre-fr" : "quaderno";
      const res = await axios.get(`${API}${endpoint}`, {
        withCredentials: true,
        responseType: "blob",
        timeout: 60000,
      });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${filenamePrefix}-${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("journal.downloadSuccess", { ext: ext.toUpperCase() }));
      localStorage.setItem("backup-reminder-last", new Date().toISOString());
      setShowBackupBanner(false);
    } catch (e) {
      toast.error(t("journal.downloadError"));
    } finally {
      setDownloading(null);
    }
  };

  const hasFrVineyard = Object.values(vineyards).some((v) => v.country === "FR");

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  return (
    <div className="min-h-screen pb-24" data-testid="journal-page">
      <Header title={t("journal.pageTitle")} subtitle={t("journal.pageSubtitle")} />
      <main className="max-w-7xl mx-auto px-4 py-5">
        <div className="flex items-center gap-2 mb-4 print-hide flex-wrap">
          <BookOpen size={22} style={{ color: "var(--primary)" }} />
          <h2 className="heading text-2xl font-extrabold">{t("journal.heading")}</h2>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <button onClick={() => downloadFile("excel")} disabled={downloading === "excel"}
              className="inline-flex items-center gap-2 text-sm px-3 py-2.5 rounded-xl font-bold shadow-sm transition-transform hover:scale-105 disabled:opacity-60"
              style={{ background: "white", color: "#166534", border: "2px solid #22c55e" }}
              data-testid="download-excel-btn"
              title={t("journal.excelButtonTitle")}>
              <FileSpreadsheet size={18} /> {downloading === "excel" ? "..." : t("journal.excelButtonLabel")}
            </button>
            <button onClick={() => downloadFile("pdf")} disabled={downloading === "pdf"}
              className="inline-flex items-center gap-2 text-sm px-3 py-2.5 rounded-xl font-bold shadow-sm transition-transform hover:scale-105 disabled:opacity-60"
              style={{ background: "white", color: "#991b1b", border: "2px solid #dc2626" }}
              data-testid="download-pdf-btn"
              title={t("journal.pdfButtonTitle")}>
              <FileText size={18} /> {downloading === "pdf" ? "..." : t("journal.pdfButtonLabel")}
            </button>
            {hasFrVineyard && (
              <button onClick={() => downloadFile("registre-fr")} disabled={downloading === "registre-fr"}
                className="inline-flex items-center gap-2 text-sm px-3 py-2.5 rounded-xl font-bold shadow-sm transition-transform hover:scale-105 disabled:opacity-60"
                style={{ background: "white", color: "#1d4ed8", border: "2px solid #3b82f6" }}
                data-testid="download-registre-fr-btn"
                title={t("journal.registreFrButtonTitle")}>
                <FileSpreadsheet size={18} /> {downloading === "registre-fr" ? "..." : t("journal.registreFrButtonLabel")}
              </button>
            )}
            <button onClick={exportPdf}
              className="inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-xl font-bold shadow-sm transition-transform hover:scale-105"
              style={{ background: "var(--gradient-vine)", color: "white", border: "2px solid var(--primary)" }}
              data-testid="print-btn">
              <Printer size={18} /> {t("journal.printButtonLabel")}
            </button>
          </div>
        </div>

        <div className="print-hide">
          <AIJournalInput vineyards={Object.values(vineyards)} onCreated={load} />
        </div>

        {showBackupBanner && (
          <div className="print-hide vs-card p-4 mb-4 vs-fade-up"
            style={{ background: "#fef3c7", border: "2px solid #f59e0b", borderLeft: "6px solid #f59e0b" }}
            data-testid="backup-banner">
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: "#f59e0b", color: "white" }}>
                <Calendar size={22} strokeWidth={2.4} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="heading font-extrabold text-base" style={{ color: "#7c2d12" }}>
                  {t("journal.backupBannerTitle")}
                </div>
                <p className="text-sm mt-1" style={{ color: "#7c2d12" }}>
                  {t("journal.backupBannerText1")} <strong>PDF</strong> {t("journal.backupBannerText2")} <strong>Excel</strong>.
                  {" "}{t("journal.backupBannerText3")}
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button onClick={() => downloadFile("pdf")} disabled={downloading === "pdf"}
                    className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg font-bold shadow-sm transition-transform hover:scale-105 disabled:opacity-60"
                    style={{ background: "#dc2626", color: "white", border: "2px solid #dc2626" }}
                    data-testid="banner-download-pdf">
                    <FileText size={16} /> {downloading === "pdf" ? "..." : t("journal.backupPdfButton")}
                  </button>
                  <button onClick={() => downloadFile("excel")} disabled={downloading === "excel"}
                    className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg font-bold shadow-sm transition-transform hover:scale-105 disabled:opacity-60"
                    style={{ background: "#16a34a", color: "white", border: "2px solid #16a34a" }}
                    data-testid="banner-download-excel">
                    <FileSpreadsheet size={16} /> {downloading === "excel" ? "..." : t("journal.backupExcelButton")}
                  </button>
                  <button onClick={() => snoozeBackup(1)}
                    className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg font-bold shadow-sm transition-transform hover:scale-105"
                    style={{ background: "white", color: "#7c2d12", border: "2px solid #f59e0b" }}
                    data-testid="banner-snooze-1d">
                    {t("journal.snoozeTomorrow")}
                  </button>
                  <button onClick={() => snoozeBackup(7)}
                    className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg font-bold shadow-sm transition-transform hover:scale-105"
                    style={{ background: "white", color: "#7c2d12", border: "2px solid #f59e0b" }}
                    data-testid="banner-snooze-7d">
                    {t("journal.snoozeWeek")}
                  </button>
                </div>
              </div>
              <button onClick={() => setShowBackupBanner(false)}
                className="shrink-0 p-1 rounded-lg"
                style={{ color: "#7c2d12" }}
                aria-label={t("journal.closeBannerAriaLabel")}
                data-testid="banner-close">
                <X size={18} />
              </button>
            </div>
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-2 mb-4 print-hide">
          {Object.keys(vineyards).length > 1 && (
            <select className="vs-input flex-1" value={filter} onChange={(e) => setFilter(e.target.value)}
              data-testid="filter-vineyard">
              <option value="all">{t("journal.allVineyardsOption")}</option>
              {Object.values(vineyards).map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          )}
          <div className="flex gap-2">
            {[["register", t("journal.tabTreatments")], ["fertilizations", t("journal.tabFertilizations")], ["advice", t("journal.tabAdvice")]].map(([key, label]) => (
              <button key={key} onClick={() => setTab(key)}
                className="px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold flex-1 sm:flex-initial"
                style={{
                  background: tab === key ? "var(--primary)" : "var(--surface)",
                  color: tab === key ? "white" : "var(--text-primary)",
                  border: `2px solid ${tab === key ? "var(--primary)" : "var(--border)"}`,
                }}
                data-testid={`tab-${key}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><div className="vs-spinner" /></div>
        ) : tab === "register" ? (
          filteredTreat.length === 0 ? (
            <div className="vs-card p-8 text-center">
              <FlaskConical size={42} style={{ color: "var(--text-muted)" }} className="mx-auto mb-3" />
              <p className="font-semibold">{t("journal.noTreatmentsRegistered")}</p>
              <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
                {t("journal.noTreatmentsHint")}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(byYear).sort((a, b) => b[0] - a[0]).map(([year, list]) => (
                <section key={year} className="vs-card p-4 sm:p-5" data-testid={`year-${year}`}>
                  <div className="text-center mb-3 pb-3 relative" style={{ borderBottom: "2px solid var(--text-primary)" }}>
                    <button type="button" onClick={() => navigate("/cestino")}
                      className="print-hide absolute top-0 right-0 p-2 rounded-lg inline-flex items-center justify-center"
                      style={{ background: "#dc2626", color: "white", border: "1.5px solid #dc2626" }}
                      aria-label={t("journal.trashAriaLabel")} title={t("journal.trashTitle")} data-testid={`treatments-trash-btn-${year}`}>
                      <Trash2 size={18} strokeWidth={2.4} />
                    </button>
                    <div className="text-xs uppercase tracking-widest font-bold" style={{ color: "var(--text-muted)" }}>
                      {t("journal.usoAgricolo")}
                    </div>
                    <div className="heading text-xl sm:text-2xl font-extrabold mt-1">
                      {t("journal.treatmentsRegisterTitle")}
                    </div>
                    <div className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
                      {t("journal.yearLabel")} <strong>{year}</strong>
                      {filter !== "all" && vineyards[filter] && (
                        <span> · {t("journal.cropLabel")}: <strong>{t("journal.grapeVine")}</strong>
                          {vineyards[filter].grape_variety && <> · {t("journal.varietaRiferimento")} <strong>{vineyards[filter].grape_variety}</strong></>}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Desktop table */}
                  <div className="hidden md:block overflow-x-auto journal-table-wrap">
                    <table className="w-full text-sm journal-table">
                      <thead>
                        <tr style={{ background: "var(--secondary)" }}>
                          <th style={thStyle}>{t("journal.table.date")}</th>
                          <th style={{ ...thStyle, width: 50 }} className="screen-only" title={t("journal.table.statusTitle")}>{t("journal.table.status")}</th>
                          <th style={thStyle}>{t("journal.table.productName")}<br />{t("journal.table.productNameSub")}</th>
                          <th style={thStyle}>{t("journal.table.quantity")}<br /><span className="font-normal">{t("journal.table.quantitySub")}</span></th>
                          <th style={thStyle}>{t("journal.table.surface")}<br /><span className="font-normal">{t("journal.table.surfaceSub")}</span></th>
                          <th style={thStyle}>{t("journal.table.diseases")}<br />{t("journal.table.diseasesSub")}</th>
                          <th style={thStyle}>{t("journal.table.operator")}<br />{t("journal.table.operatorSub")}</th>
                          <th style={thStyle}>{t("journal.table.signature")}</th>
                          <th style={thStyle} className="screen-only">{t("journal.table.vineyard")}</th>
                          <th style={{ ...thStyle, width: 64 }} className="print-hide">{t("journal.table.actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.map((row, idx) => {
                          const prev = idx > 0 ? list[idx - 1] : null;
                          const isNewDate = !prev || new Date(prev.treatment_date).toDateString() !== new Date(row.treatment_date).toDateString();
                          const worst = rowWorstStatus(row.product_name, ministryStatus);

                          return (
                            <Fragment key={row.id}>
                              {idx > 0 && isNewDate && (
                                <tr aria-hidden="true" className="date-sep-row print-hide">
                                  <td colSpan={10} />
                                </tr>
                              )}
                              <tr data-testid={`register-row-${row.id}`} className="journal-row">
                                <td style={tdStyle}>{fmtDate(row.treatment_date, dateLocale)}</td>
                                <td style={{ ...tdStyle, padding: 4 }} className="screen-only"><StatusDot status={worst} /></td>
                                <td style={tdStyle}>
                                  <div className="font-bold">{row.product_name}</div>
                                  {row.active_ingredient && (
                                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>{row.active_ingredient}</div>
                                  )}
                                  <MinistryBadges productName={row.product_name} statusMap={ministryStatus} />
                                </td>
                                <td style={tdStyle}>{row.quantity_used || "—"}</td>
                                <td style={tdStyle}>{row.surface_treated != null ? row.surface_treated : "—"}</td>
                                <td style={tdStyle}>
                                  {(row.target_diseases && row.target_diseases.length > 0) ? row.target_diseases.join(", ") : "—"}
                                </td>
                                <td style={tdStyle}>{row.operator_name || "—"}</td>
                                <td style={{ ...tdStyle, minWidth: 90 }}></td>
                                <td style={tdStyle} className="screen-only">
                                  {vineyardsLabel(t, vineyards, row.vineyard_id, row.additional_vineyard_ids)}
                                </td>
                                <td style={{ ...tdStyle, padding: "6px" }} className="print-hide">
                                  <ActionButtons onEdit={() => openEditTreatment(row)} onDelete={() => deleteTreatment(row.id)} />
                                </td>
                              </tr>
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile cards */}
                  <div className="md:hidden space-y-3">
                    {list.map((row, idx) => {
                      const prev = idx > 0 ? list[idx - 1] : null;
                      const isNewDate = !prev || new Date(prev.treatment_date).toDateString() !== new Date(row.treatment_date).toDateString();
                      const worst = rowWorstStatus(row.product_name, ministryStatus);
                      return (
                        <Fragment key={row.id}>
                          {idx > 0 && isNewDate && (
                            <div aria-hidden="true" style={{ height: 4, background: "var(--primary)", borderRadius: 2, margin: "8px 0" }} />
                          )}
                          <div className="p-3 rounded-lg"
                            style={{ border: "2px solid var(--border)", background: "var(--surface)" }}
                            data-testid={`register-card-${row.id}`}>
                            <div className="flex items-start justify-between mb-2 pb-2" style={{ borderBottom: "1px solid var(--border)" }}>
                              <div>
                                <div className="heading font-extrabold text-base flex items-center gap-2"><StatusDot status={worst} /> {row.product_name}</div>
                                {row.active_ingredient && (
                                  <div className="text-xs" style={{ color: "var(--text-muted)" }}>{row.active_ingredient}</div>
                                )}
                                <MinistryBadges productName={row.product_name} statusMap={ministryStatus} />
                              </div>
                              <div className="flex flex-col items-end gap-2">
                                <div className="text-xs font-bold" style={{ color: "var(--primary)" }}>
                                  {fmtDate(row.treatment_date, dateLocale)}
                                </div>
                                <ActionButtons onEdit={() => openEditTreatment(row)} onDelete={() => deleteTreatment(row.id)} />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <KV label={t("journal.kv.quantity")} value={row.quantity_used || "—"} />
                              <KV label={t("journal.kv.surface")} value={row.surface_treated != null ? row.surface_treated : "—"} />
                              <KV label={t("journal.kv.operator")} value={row.operator_name || "—"} span />
                              <KV label={t("journal.kv.diseases")} value={(row.target_diseases || []).join(", ") || "—"} span />
                              {vineyards[row.vineyard_id] && <KV label={t("journal.kv.vineyards")} value={vineyardsLabel(t, vineyards, row.vineyard_id, row.additional_vineyard_ids)} span />}
                            </div>
                            {row.notes && (
                              <div className="text-xs italic mt-2 pt-2" style={{ color: "var(--text-muted)", borderTop: "1px dashed var(--border)" }}>
                                {row.notes}
                              </div>
                            )}
                          </div>
                        </Fragment>
                      );
                    })}
                  </div>

                  <div className="text-xs mt-4 pt-2" style={{ color: "var(--text-muted)", borderTop: "1px solid var(--border)" }}>
                    {t("journal.treatmentCount", { count: list.length })}
                  </div>
                </section>
              ))}
            </div>
          )
        ) : tab === "fertilizations" ? (
          filteredFert.length === 0 ? (
            <div className="vs-card p-8 text-center">
              <Sprout size={42} style={{ color: "var(--text-muted)" }} className="mx-auto mb-3" />
              <p className="font-semibold">{t("journal.noFertRegistered")}</p>
              <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
                {t("journal.noFertHint")}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(fertByYear).sort((a, b) => b[0] - a[0]).map(([year, list]) => (
                <section key={year} className="vs-card p-4 sm:p-5" data-testid={`fert-year-${year}`}>
                  <div className="text-center mb-3 pb-3 relative" style={{ borderBottom: "2px solid var(--text-primary)" }}>
                   <button type="button" onClick={() => navigate("/cestino")}
                      className="print-hide absolute top-0 right-0 p-2 rounded-lg inline-flex items-center justify-center"
                      style={{ background: "#dc2626", color: "white", border: "1.5px solid #dc2626" }}
                      aria-label={t("journal.trashAriaLabel")} title={t("journal.trashTitle")} data-testid={`fert-trash-btn-${year}`}>
                      <Trash2 size={18} strokeWidth={2.4} />
                    </button>
                    <div className="text-xs uppercase tracking-widest font-bold" style={{ color: "var(--text-muted)" }}>
                      {t("journal.usoAgricolo")}
                    </div>
                    <div className="heading text-xl sm:text-2xl font-extrabold mt-1">
                      {t("journal.registroConcimazioni")}
                    </div>
                    <div className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
                      {t("journal.yearLabel")} <strong>{year}</strong>
                    </div>
                  </div>

                  <div className="hidden md:block overflow-x-auto journal-table-wrap">
                    <table className="w-full text-sm journal-table">
                      <thead>
                        <tr style={{ background: "var(--secondary)" }}>
                          <th style={thStyle}>{t("journal.table.date")}</th>
                          <th style={thStyle}>{t("journal.fertTable.product")}<br /><span className="font-normal">{t("journal.fertTable.productSub")}</span></th>
                          <th style={thStyle}>{t("journal.fertTable.type")}</th>
                          <th style={thStyle}>{t("journal.fertTable.quantity")}</th>
                          <th style={thStyle}>{t("journal.fertTable.surface")}<br /><span className="font-normal">{t("journal.fertTable.surfaceSub")}</span></th>
                          <th style={thStyle}>{t("journal.fertTable.method")}</th>
                          <th style={thStyle}>{t("journal.kv.operator")}</th>
                          <th style={thStyle}>{t("journal.table.signature")}</th>
                          <th style={thStyle} className="screen-only">{t("journal.table.vineyard")}</th>
                          <th style={{ ...thStyle, width: 64 }} className="print-hide">{t("journal.table.actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.map((f, idx) => {
                          const prev = idx > 0 ? list[idx - 1] : null;
                          const isNewDate = !prev || new Date(prev.fertilization_date).toDateString() !== new Date(f.fertilization_date).toDateString();
                          return (
                            <Fragment key={f.id}>
                              {idx > 0 && isNewDate && (
                                <tr aria-hidden="true" className="date-sep-row print-hide">
                                  <td colSpan={10} />
                                </tr>
                              )}
                              <tr data-testid={`fert-row-${f.id}`} className="journal-row">
                                <td style={tdStyle}>{fmtDate(f.fertilization_date, dateLocale)}</td>
                                <td style={tdStyle}>
                                  <div className="font-bold">{f.product_name}</div>
                                  {f.objective && (
                                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>{f.objective}</div>
                                  )}
                                </td>
                                <td style={tdStyle}>{f.product_type || "—"}</td>
                                <td style={tdStyle}>{f.quantity_used || "—"}</td>
                                <td style={tdStyle}>{f.surface_treated != null ? f.surface_treated : "—"}</td>
                                <td style={tdStyle}>{f.application_method || "—"}</td>
                                <td style={tdStyle}>{f.operator_name || "—"}</td>
                                <td style={{ ...tdStyle, minWidth: 90 }}></td>
                                <td style={tdStyle} className="screen-only">
                                  {vineyardsLabel(t, vineyards, f.vineyard_id, f.additional_vineyard_ids)}
                                </td>
                                <td style={{ ...tdStyle, padding: "6px" }} className="print-hide">
                                  <ActionButtons onEdit={() => openEditFertilization(f)} onDelete={() => deleteFertilization(f.id)} />
                                </td>
                              </tr>
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="md:hidden space-y-3">
                    {list.map((f, idx) => {
                      const prev = idx > 0 ? list[idx - 1] : null;
                      const isNewDate = !prev || new Date(prev.fertilization_date).toDateString() !== new Date(f.fertilization_date).toDateString();
                      return (
                        <Fragment key={f.id}>
                          {idx > 0 && isNewDate && (
                            <div aria-hidden="true" style={{ height: 4, background: "var(--primary)", borderRadius: 2, margin: "8px 0" }} />
                          )}
                          <div className="p-3 rounded-lg"
                            style={{ border: "2px solid var(--border)", background: "var(--surface)" }}
                            data-testid={`fert-card-${f.id}`}>
                            <div className="flex items-start justify-between mb-2 pb-2" style={{ borderBottom: "1px solid var(--border)" }}>
                              <div>
                                <div className="heading font-extrabold text-base">{f.product_name}</div>
                                {f.product_type && (
                                  <span className="vs-pill text-xs mt-1" style={{ background: "var(--primary)", color: "white", borderColor: "transparent" }}>
                                    {f.product_type}
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-2">
                                <div className="text-xs font-bold" style={{ color: "var(--primary)" }}>
                                  {fmtDate(f.fertilization_date, dateLocale)}
                                </div>
                                <ActionButtons onEdit={() => openEditFertilization(f)} onDelete={() => deleteFertilization(f.id)} />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <KV label={t("journal.kv.quantity")} value={f.quantity_used || "—"} />
                              <KV label={t("journal.kv.surface")} value={f.surface_treated != null ? f.surface_treated : "—"} />
                              {f.application_method && <KV label={t("journal.fertTable.method")} value={f.application_method} span />}
                              {f.objective && <KV label={t("journal.fields.objective")} value={f.objective} span />}
                              <KV label={t("journal.kv.operator")} value={f.operator_name || "—"} span />
                              {vineyards[f.vineyard_id] && <KV label={t("journal.kv.vineyards")} value={vineyardsLabel(t, vineyards, f.vineyard_id, f.additional_vineyard_ids)} span />}
                            </div>
                            {f.notes && (
                              <div className="text-xs italic mt-2 pt-2" style={{ color: "var(--text-muted)", borderTop: "1px dashed var(--border)" }}>
                                {f.notes}
                              </div>
                            )}
                          </div>
                        </Fragment>
                      );
                    })}
                  </div>

                  <div className="text-xs mt-4 pt-2" style={{ color: "var(--text-muted)", borderTop: "1px solid var(--border)" }}>
                    {t("journal.fertCount", { count: list.length })}
                  </div>
                </section>
              ))}
            </div>
          )
        ) : (
          filteredAdvice.length === 0 ? (
            <div className="vs-card p-8 text-center">
              <Sprout size={42} style={{ color: "var(--text-muted)" }} className="mx-auto mb-3" />
              <p className="font-semibold">{t("journal.noAdviceRegistered")}</p>
              <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
                {t("journal.noAdviceHint")}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredAdvice.map((x) => {
                const v = vineyards[x.vineyard_id];
                const reco = x.result?.recommendations || [];
                const isOpen = !!expanded[x.id];
                return (
                  <div key={x.id} className="vs-card p-4 vs-fade-up" style={{ borderLeft: "4px solid var(--primary)" }} data-testid={`advice-${x.id}`}>
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Sprout size={18} style={{ color: "var(--primary)" }} />
                        <span className="heading font-bold">{t("journal.adviceTitle")}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold" style={{ color: "var(--primary)" }}>
                          <Calendar size={12} className="inline mr-1" />
                          {fmtDate(x.created_at, dateLocale)}
                        </span>
                        <ActionButtons onDelete={() => deleteAdvice(x.id)} />
                      </div>
                    </div>
                    {v && <div className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>
                      <MapPin size={12} className="inline mr-1" />{v.name}
                    </div>}
                    {x.result?.summary && (
                      <p className="text-sm mb-2" style={{ color: "var(--text-secondary)" }}>{x.result.summary}</p>
                    )}
                    {reco.length > 0 && (
                      <>
                        <button onClick={() => toggle(x.id)}
                          className="text-xs font-semibold inline-flex items-center gap-1 mt-1"
                          style={{ color: "var(--primary)" }}
                          data-testid={`advice-toggle-${x.id}`}>
                          {isOpen ? <><ChevronUp size={14} /> {t("journal.hideDetails")}</> : <><ChevronDown size={14} /> {t("journal.viewRecommendations", { count: reco.length })}</>}
                        </button>
                        {isOpen && (
                          <div className="mt-2 space-y-2">
                            {reco.map((r, i) => (
                              <div key={i} className="p-2 rounded-lg text-sm" style={{ background: "var(--secondary)", border: "1px solid var(--border)" }}>
                                <div className="font-bold">{r.product_type}</div>
                                {r.when && <div className="text-xs" style={{ color: "var(--grape)" }}>📅 {r.when}</div>}
                                {r.dose && <div className="text-xs">⚖️ {r.dose}</div>}
                                {r.reason && <div className="text-xs italic mt-1" style={{ color: "var(--text-muted)" }}>{r.reason}</div>}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}

        <p className="text-xs mt-4 print-hide" style={{ color: "var(--text-muted)" }}>
          💡 {t("journal.printDisclaimer1")} <strong>{t("journal.printLabel")}</strong> {t("journal.printDisclaimer2")}
          <strong> {t("journal.registerLabel")}</strong> {t("journal.printDisclaimer3")}
          {" "}<strong>{t("journal.signatureLabel")}</strong> {t("journal.printDisclaimer4")}
        </p>
      </main>
      <BottomNav />

      {editTarget && editItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 print-hide"
          style={{ background: "rgba(0,0,0,0.55)" }}
          onClick={(e) => { if (e.target === e.currentTarget) closeEdit(); }}>
          <div className="vs-card w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto" style={{ border: "2px solid var(--primary)" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="heading font-extrabold text-lg">
                {editTarget === "treatment" ? t("journal.editTreatmentTitle") : t("journal.editFertTitle")}
              </h3>
              <button onClick={closeEdit} className="p-1"><X size={20} /></button>
            </div>

            <div className="space-y-3 text-sm">
              {editTarget === "treatment" ? (
                <>
                  <Field label={t("journal.fields.date")} type="date" value={editForm.treatment_date}
                    onChange={(v) => setEditForm((f) => ({ ...f, treatment_date: v }))} />
                  <Field label={t("journal.fields.productPhyto")} value={editForm.product_name}
                    onChange={(v) => setEditForm((f) => ({ ...f, product_name: v }))} />
                  <Field label={t("treatment.activeIngredientLabel")} value={editForm.active_ingredient}
                    onChange={(v) => setEditForm((f) => ({ ...f, active_ingredient: v }))} />
                  <Field label={t("journal.fields.quantityLiters")} value={editForm.quantity_used}
                    onChange={(v) => setEditForm((f) => ({ ...f, quantity_used: v }))} />
                  <Field label={t("journal.fields.surfaceHectares")} type="number" value={editForm.surface_treated}
                    onChange={(v) => setEditForm((f) => ({ ...f, surface_treated: v }))} />
                  <Field label={t("journal.fields.diseasesComma")} value={editForm.target_diseases}
                    onChange={(v) => setEditForm((f) => ({ ...f, target_diseases: v }))} />
                  <Field label={t("treatment.operatorLabel")} value={editForm.operator_name}
                    onChange={(v) => setEditForm((f) => ({ ...f, operator_name: v }))} />
                  {vineyards[editItem.vineyard_id]?.country === "FR" && (
                    <>
                      <p className="text-xs font-bold uppercase tracking-wider pt-2" style={{ color: "var(--text-muted)" }}>
                        {t("treatment.fr.sectionTitleShort")}
                      </p>
                      <Field label={t("treatment.fr.siretExploitantLabel")} value={editForm.siret_exploitant}
                        onChange={(v) => setEditForm((f) => ({ ...f, siret_exploitant: v }))} />
                      <Field label={t("treatment.fr.siretPrestataireLabel")} value={editForm.siret_prestataire}
                        onChange={(v) => setEditForm((f) => ({ ...f, siret_prestataire: v }))} />
                      <Field label={t("treatment.fr.ilotLabel")} value={editForm.ilot}
                        onChange={(v) => setEditForm((f) => ({ ...f, ilot: v }))} />
                      <Field label={t("treatment.fr.parcelleLabel")} value={editForm.numero_parcella}
                        onChange={(v) => setEditForm((f) => ({ ...f, numero_parcella: v }))} />
                      <Field label={t("treatment.fr.inseeLabelJournal")} value={editForm.codice_insee_comune}
                        onChange={(v) => setEditForm((f) => ({ ...f, codice_insee_comune: v }))} />
                      <Field label={t("treatment.fr.bbchLabel")} value={editForm.bbch_stage}
                        onChange={(v) => setEditForm((f) => ({ ...f, bbch_stage: v }))} />
                      <Field label={t("treatment.fr.eppoLabelInline")} value={editForm.eppo_target_codes}
                        onChange={(v) => setEditForm((f) => ({ ...f, eppo_target_codes: v }))} />
                      <Field label={t("treatment.fr.startTimeLabel")} type="time" value={editForm.treatment_start_time}
                        onChange={(v) => setEditForm((f) => ({ ...f, treatment_start_time: v }))} />
                      <Field label={t("treatment.fr.endTimeLabel")} type="time" value={editForm.treatment_end_time}
                        onChange={(v) => setEditForm((f) => ({ ...f, treatment_end_time: v }))} />
                    </>
                  )}
                  <Field label={t("treatment.notesLabel")} value={editForm.notes} textarea
                    onChange={(v) => setEditForm((f) => ({ ...f, notes: v }))} />
                </>
              ) : (
                <>
                  <Field label={t("journal.fields.date")} type="date" value={editForm.fertilization_date}
                    onChange={(v) => setEditForm((f) => ({ ...f, fertilization_date: v }))} />
                  <Field label={t("journal.fields.product")} value={editForm.product_name}
                    onChange={(v) => setEditForm((f) => ({ ...f, product_name: v }))} />
                  <Field label={t("journal.fields.type")} value={editForm.product_type}
                    onChange={(v) => setEditForm((f) => ({ ...f, product_type: v }))} />
                  <Field label={t("journal.kv.quantity")} value={editForm.quantity_used}
                    onChange={(v) => setEditForm((f) => ({ ...f, quantity_used: v }))} />
                  <Field label={t("journal.fields.surfaceHa")} type="number" value={editForm.surface_treated}
                    onChange={(v) => setEditForm((f) => ({ ...f, surface_treated: v }))} />
                  <Field label={t("journal.fields.applicationMethod")} value={editForm.application_method}
                    onChange={(v) => setEditForm((f) => ({ ...f, application_method: v }))} />
                  <Field label={t("journal.fields.objective")} value={editForm.objective}
                    onChange={(v) => setEditForm((f) => ({ ...f, objective: v }))} />
                  <Field label={t("journal.kv.operator")} value={editForm.operator_name}
                    onChange={(v) => setEditForm((f) => ({ ...f, operator_name: v }))} />
                  <Field label={t("treatment.notesLabel")} value={editForm.notes} textarea
                    onChange={(v) => setEditForm((f) => ({ ...f, notes: v }))} />
                </>
              )}
            </div>

            <div className="flex gap-3 mt-5">
              <button onClick={closeEdit}
                className="flex-1 py-2.5 rounded-xl font-bold text-sm"
                style={{ background: "var(--surface)", border: "2px solid var(--border)", color: "var(--text-primary)" }}>
                {t("dashboard.form.cancelButton")}
              </button>
              <button onClick={saveEdit} disabled={saving}
                className="flex-1 py-2.5 rounded-xl font-bold text-sm inline-flex items-center justify-center gap-2"
                style={{ background: "var(--gradient-vine)", color: "white", border: "2px solid var(--primary)", opacity: saving ? 0.7 : 1 }}>
                <Check size={16} /> {saving ? t("journal.saving") : t("dashboard.form.submitUpdate")}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media screen {
          .print-only { display: none !important; }
        }
        .journal-table { border: 2px solid var(--text-primary); border-collapse: collapse; }
        .journal-row:hover td { background: rgba(0,0,0,0.025); }
        .date-sep-row td { height: 6px; padding: 0; background: var(--primary); border: none; }
        @media print {
          @page { size: A4 landscape; margin: 12mm 10mm; }
          html, body { background: white !important; }
          .print-hide, nav, header, .vs-bottom-nav { display: none !important; }
          .print-only { display: block !important; }
          .screen-only { display: none !important; }
          main { padding: 0 !important; max-width: none !important; }
          .vs-card { border: 1px solid #000 !important; box-shadow: none !important; break-inside: avoid; padding: 8mm !important; margin-bottom: 6mm !important; background: white !important; }
          .heading { color: #000 !important; }
          .journal-table-wrap { display: block !important; }
          .journal-table-wrap table { font-size: 11pt !important; }
          .journal-table-wrap thead tr { background: #f1ede2 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .journal-table-wrap th, .journal-table-wrap td { border: 1px solid #000 !important; color: #000 !important; padding: 6px 8px !important; }
          .vs-pill { border: 1px solid #000 !important; background: white !important; color: #000 !important; }
          .md\\:hidden { display: none !important; }
          .hidden.md\\:block { display: block !important; }
        }
        @keyframes pulseBadge {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.5); }
          50% { box-shadow: 0 0 0 6px rgba(220, 38, 38, 0); }
        }
        .ministry-badge-revoked { animation: pulseBadge 1.8s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

const thStyle = {
  border: "1.5px solid var(--text-primary)",
  padding: "8px 10px",
  textAlign: "center",
  fontSize: "12px",
  fontWeight: 700,
  lineHeight: 1.2,
};

const tdStyle = {
  border: "1px solid var(--text-primary)",
  padding: "8px 10px",
  textAlign: "center",
  fontSize: "13px",
  verticalAlign: "middle",
};

function Field({ label, value, onChange, type = "text", textarea }) {
  const cls = "vs-input w-full mt-1";
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</label>
      {textarea ? (
        <textarea className={cls} rows={3} value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input className={cls} type={type} value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

function KV({ label, value, span }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--text-muted)" }}>{label}</div>
      <div className="font-semibold text-sm">{value}</div>
    </div>
  );
}

function vineyardsLabel(t, vineyards, primaryId, additionalIds) {
  const primary = vineyards[primaryId];
  const additional = (additionalIds || []).map((id) => vineyards[id]).filter(Boolean);
  if (additional.length === 0) return primary?.name || "—";
  const totalCount = 1 + additional.length;
  const allVineyardCount = Object.keys(vineyards).length;
  if (totalCount === allVineyardCount && allVineyardCount > 1) return t("journal.allVineyardsSummary", { count: totalCount });
  if (additional.length <= 2) return [primary?.name, ...additional.map((v) => v.name)].filter(Boolean).join(" + ");
  return `${primary?.name || "?"} ${t("journal.othersSummary", { count: additional.length })}`;
}

const ACTION_BTN_BASE = {
  width: 32, height: 32, borderRadius: 6,
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  border: "none", cursor: "pointer",
  boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
  transition: "transform 0.15s",
};

function ActionButtons({ onEdit, onDelete }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-1.5 print-hide">
      {onEdit && (
        <button onClick={onEdit} aria-label={t("dashboard.card.editButton")} data-testid="action-edit"
          style={{ ...ACTION_BTN_BASE, background: "var(--primary)" }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.1)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}>
          <Pencil size={16} color="white" strokeWidth={2.5} />
        </button>
      )}
      {onDelete && (
        <button onClick={onDelete} aria-label={t("dashboard.card.deleteAriaLabel")} data-testid="action-delete"
          style={{ ...ACTION_BTN_BASE, background: "#dc2626" }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.1)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}>
          <Trash2 size={16} color="white" strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}

function StatusDot({ status }) {
  const { t } = useTranslation();
  const colors = {
    ok:          { bg: "#22c55e", title: t("journal.status.authorized") },
    scaduto:     { bg: "#f59e0b", title: t("journal.status.expired") },
    revocato:    { bg: "#dc2626", title: t("journal.status.revoked") },
    sconosciuto: { bg: "#cbd5e1", title: t("journal.status.notInCatalog") },
  };
  const c = colors[status] || colors.sconosciuto;
  return (
    <span title={c.title} aria-label={c.title} data-testid={`status-dot-${status}`} style={{
      display: "inline-block",
      width: 16, height: 16, borderRadius: "50%",
      background: c.bg,
      border: "2px solid white",
      boxShadow: status === "revocato"
        ? "0 0 0 2px #dc2626, 0 0 6px rgba(220,38,38,0.45)"
        : "0 0 0 1.5px rgba(0,0,0,0.25)",
    }} />
  );
}

const BADGE_STYLE = {
  ok:        { bg: "#dcfce7", color: "#166534", border: "#22c55e", Icon: ShieldCheck, animClass: "" },
  revocato:  { bg: "#fee2e2", color: "#991b1b", border: "#dc2626", Icon: ShieldAlert, animClass: "ministry-badge-revoked" },
  scaduto:   { bg: "#fef3c7", color: "#92400e", border: "#f59e0b", Icon: ShieldOff, animClass: "" },
  sconosciuto: { bg: "#f1f5f9", color: "#475569", border: "#cbd5e1", Icon: HelpCircle, animClass: "" },
};

function MinistryBadges({ productName, statusMap }) {
  const { t } = useTranslation();
  const names = splitProductNames(productName);
  if (names.length === 0) return null;
  const showName = names.length > 1;
  return (
    <div className="flex flex-wrap gap-1 mt-1.5 justify-center print-hide">
      {names.map((n, i) => {
        const info = statusMap[n] || { status: "sconosciuto", label: "—" };
        const s = BADGE_STYLE[info.status] || BADGE_STYLE.sconosciuto;
        const Icon = s.Icon;
        const title = info.denominazione_ufficiale
          ? `${info.denominazione_ufficiale}${info.num_registrazione ? t("journal.ministryBadge.regSuffix", { num: info.num_registrazione }) : ""}${info.data_scadenza ? t("journal.ministryBadge.expSuffix", { date: info.data_scadenza }) : ""}`
          : t("journal.ministryBadge.notFound");
        return (
          <span key={i} title={title} className={s.animClass}
            data-testid={`ministry-badge-${info.status}`}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "2px 7px", borderRadius: 999,
              background: s.bg, color: s.color, border: `1px solid ${s.border}`,
              fontSize: 10, fontWeight: 700, lineHeight: 1.4,
              whiteSpace: "nowrap",
            }}>
            <Icon size={11} strokeWidth={2.5} />
            {showName ? `${n}: ${info.label}` : info.label}
          </span>
        );
      })}
    </div>
  );
}
